// AcademyPage — Education / Learning center
// Layout:
//   - Top nav: < Back | Academy
//   - Hero: progress + streak
//   - Continue learning section
//   - Course categories
//   - Featured courses
//   - Live webinars
//   - Quizzes / achievements

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserAvatar, MerchantLogo } from '../../components/Brand';

interface Course {
  id: string;
  title: string;
  description: string;
  category: 'Beginner' | 'Trading' | 'Risk' | 'Psychology' | 'Advanced';
  level: 'Beginner' | 'Intermediate' | 'Advanced';
  duration: string;
  lessons: number;
  completed: number;
  instructor: string;
  instructorRole: string;
  rating: number;
  enrolled: number;
  cover: string; // emoji
  accent: string;
  locked?: boolean;
  requiredTier?: 'Mid' | 'Professional';
}

const COURSES: Course[] = [
  { id: 'c1', title: 'Prop Firm Trading Fundamentals', description: 'How funded accounts work, drawdown rules, and your first challenge', category: 'Beginner', level: 'Beginner', duration: '2h 30m', lessons: 12, completed: 12, instructor: 'Sarah Chen', instructorRole: 'Senior Trader · 8 yrs', rating: 4.9, enrolled: 8420, cover: '🎓', accent: '#34C759' },
  { id: 'c2', title: 'Reading the DOM Like a Pro',       description: 'Order flow, iceberg orders, and reading the depth of market',         category: 'Trading',   level: 'Intermediate', duration: '3h 15m', lessons: 18, completed: 7, instructor: 'Marcus Kim', instructorRole: 'Prop Trader · 6 yrs',  rating: 4.8, enrolled: 5340, cover: '📊', accent: '#007AFF' },
  { id: 'c3', title: 'Risk Management Masterclass',     description: 'Position sizing, R-multiples, and surviving drawdowns',                category: 'Risk',      level: 'Intermediate', duration: '2h 45m', lessons: 14, completed: 14, instructor: 'Diego Voss', instructorRole: 'Risk Manager · 12 yrs', rating: 5.0, enrolled: 6210, cover: '🛡️', accent: '#FF9500' },
  { id: 'c4', title: 'Trading Psychology Deep Dive',     description: 'Master your emotions, build discipline, recover from losses',       category: 'Psychology', level: 'Advanced',   duration: '4h',      lessons: 22, completed: 3, instructor: 'Dr. Mia Park',  instructorRole: 'Sports Psychologist',   rating: 4.9, enrolled: 3210, cover: '🧠', accent: '#AF52DE' },
  { id: 'c5', title: 'AI Bot Strategy Engineering',     description: 'Build, backtest, and deploy automated trading strategies',         category: 'Advanced',  level: 'Advanced',   duration: '5h 30m', lessons: 28, completed: 0, instructor: 'Alex Rivera',  instructorRole: 'Quant Engineer',        rating: 4.7, enrolled: 1890, cover: '🤖', accent: '#1C1C1E', locked: true, requiredTier: 'Professional' },
  { id: 'c6', title: 'Scalping the ES with Volume Profile', description: 'Day trading strategy for E-mini S&P with volume profile',       category: 'Trading',   level: 'Advanced',   duration: '3h 45m', lessons: 20, completed: 0, instructor: 'Yuki Tanaka',  instructorRole: 'Day Trader · 5 yrs',    rating: 4.8, enrolled: 2450, cover: '⚡', accent: '#FF3B30' },
];

const WEBINARS = [
  { id: 'w1', title: 'Live: Trading NFP like a Pro',         host: 'Marcus Chen',     date: 'Today 14:00 ET', spots: 234, accent: '#34C759' },
  { id: 'w2', title: 'Q&A with funded traders',              host: 'Top 10 leaderboard', date: 'Tomorrow 18:00 ET', spots: 412, accent: '#007AFF' },
  { id: 'w3', title: 'Bot workshop: Backtesting your strategy', host: 'Alex Rivera',    date: 'Friday 16:00 ET', spots: 89,  accent: '#FF9500' },
];

const QUIZZES = [
  { id: 'q1', title: 'EOD Balance drawdown explained',  questions: 8,  duration: '5 min', reward: 25 },
  { id: 'q2', title: 'Risk:reward fundamentals',          questions: 10, duration: '7 min', reward: 35 },
  { id: 'q3', title: 'DOM reading test',                 questions: 12, duration: '10 min', reward: 50 },
];

export function AcademyPage() {
  const nav = useNavigate();
  const [tab, setTab] = useState<'All' | 'Beginner' | 'Trading' | 'Risk' | 'Psychology' | 'Advanced'>('All');
  const courses = COURSES.filter((c) => tab === 'All' || c.category === tab);
  const continueLearning = COURSES.find((c) => c.completed > 0 && c.completed < c.lessons);
  const completed = COURSES.reduce((s, c) => s + c.completed, 0);
  const total = COURSES.reduce((s, c) => s + c.lessons, 0);
  const pct = Math.round((completed / total) * 100);

  return (
    <div className="min-h-screen bg-tf-surface-1 pb-8">
      <div className="sticky top-0 z-10 flex h-11 items-center justify-between border-b border-tf-border ios26-navbar px-2 backdrop-blur-xl">
        <button onClick={() => nav(-1)} data-testid="back" className="flex h-9 w-9 items-center justify-center text-tf-blue haptic">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M15 5l-7 7 7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
        <div className="absolute left-1/2 -translate-x-1/2 text-[15px] font-semibold">Academy</div>
        <button className="flex h-9 w-9 items-center justify-center text-tf-blue">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="12" cy="12" r="9" /><path d="M9 9a3 3 0 0 1 6 0c0 1-1 2-3 3-1 .5-1 1-1 2M12 17h.01" strokeLinecap="round" /></svg>
        </button>
      </div>

      {/* Hero — progress */}
      <div className="mx-4 mt-4">
        <div className="ios26-hero">
          <div className="flex items-center gap-3">
            <div className="flex h-14 w-14 items-center justify-center rounded-[16px] text-[32px]" style={{ background: 'linear-gradient(135deg, #34C759 0%, #1E7E34 100%)' }}>🎓</div>
            <div>
              <div className="text-[11px] uppercase tracking-wider text-tf-text-3">Learning progress</div>
              <div className="dynamic-title text-tf-text-1 tabular-nums">{pct}%</div>
              <div className="text-[12px] text-tf-text-3">{completed} of {total} lessons</div>
            </div>
          </div>
          <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-tf-surface-1">
            <div className="h-full rounded-full bg-gradient-to-r from-[#34C759] to-[#007AFF]" style={{ width: pct + '%' }} />
          </div>
        </div>
      </div>

      {/* Continue learning */}
      {continueLearning && (
        <>
          <h2 className="mt-5 px-4 pb-2 text-[13px] font-normal uppercase tracking-wide text-tf-text-3">Continue learning</h2>
          <div className="mx-4 ios26-card p-4 active:scale-[0.99] transition cursor-pointer" onClick={() => alert('Open ' + continueLearning.title)} data-testid="continue-learning">
            <div className="flex items-center gap-3">
              <div className="flex h-14 w-14 items-center justify-center rounded-[14px] text-[28px]" style={{ backgroundColor: continueLearning.accent + '18' }}>{continueLearning.cover}</div>
              <div className="flex-1 min-w-0">
                <div className="text-[11px] uppercase tracking-wider text-tf-text-3">{continueLearning.category}</div>
                <div className="text-[15px] font-bold text-tf-text-1">{continueLearning.title}</div>
                <div className="text-[11px] text-tf-text-3 mt-0.5">{continueLearning.completed} of {continueLearning.lessons} lessons · {continueLearning.duration}</div>
                <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-tf-surface-1">
                  <div className="h-full rounded-full bg-tf-blue" style={{ width: ((continueLearning.completed / continueLearning.lessons) * 100) + '%' }} />
                </div>
              </div>
              <svg viewBox="0 0 24 24" className="h-5 w-5 text-tf-text-muted" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
            </div>
          </div>
        </>
      )}

      {/* Categories pills */}
      <h2 className="mt-5 px-4 pb-2 text-[13px] font-normal uppercase tracking-wide text-tf-text-3">Courses</h2>
      <div className="px-4 flex items-center gap-2 overflow-x-auto">
        {(['All', 'Beginner', 'Trading', 'Risk', 'Psychology', 'Advanced'] as const).map((c) => (
          <button
            key={c}
            onClick={() => setTab(c)}
            data-testid={`academy-tab-${c}`}
            className={['shrink-0 rounded-full px-3.5 py-1.5 text-[13px] font-semibold transition',
              tab === c ? 'bg-tf-text-1 text-white' : 'bg-tf-surface-1 text-tf-text-1'].join(' ')}
          >
            {c}
          </button>
        ))}
      </div>

      {/* Course list */}
      <div className="mx-4 mt-3 space-y-2">
        {courses.map((c) => <CourseCard key={c.id} course={c} />)}
      </div>

      {/* Live webinars */}
      <h2 className="mt-5 px-4 pb-2 text-[13px] font-normal uppercase tracking-wide text-tf-text-3">Live webinars</h2>
      <div className="mx-4 space-y-2">
        {WEBINARS.map((w) => (
          <div key={w.id} className="ios26-card p-3 flex items-center gap-3" data-testid={`webinar-${w.id}`}>
            <div className="flex h-12 w-12 items-center justify-center rounded-[12px] text-[24px] relative" style={{ backgroundColor: w.accent + '18' }}>
              📡
              <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-[#FF3B30] text-[8px] font-bold text-white">●</span>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[14px] font-semibold text-tf-text-1 truncate">{w.title}</div>
              <div className="text-[11px] text-tf-text-3">{w.host} · {w.date} · {w.spots} spots left</div>
            </div>
            <button className="rounded-full bg-[#FF3B30] px-3 py-1 text-[11px] font-semibold text-white active:opacity-40">Join</button>
          </div>
        ))}
      </div>

      {/* Quizzes */}
      <h2 className="mt-5 px-4 pb-2 text-[13px] font-normal uppercase tracking-wide text-tf-text-3">Quizzes</h2>
      <div className="mx-4 grid grid-cols-2 gap-2">
        {QUIZZES.map((q) => (
          <div key={q.id} className="ios26-card p-3" data-testid={`quiz-${q.id}`}>
            <div className="text-[13px] font-semibold text-tf-text-1 line-clamp-2">{q.title}</div>
            <div className="mt-1 text-[11px] text-tf-text-3">{q.questions} questions · {q.duration}</div>
            <div className="mt-2 flex items-center justify-between">
              <span className="rounded-full bg-[#34C759]/15 px-2 py-0.5 text-[10px] font-bold text-tf-profit">+${q.reward}</span>
              <button className="rounded-full bg-tf-text-1 px-3 py-1 text-[11px] font-semibold text-white active:opacity-40">Start</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function CourseCard({ course }: { course: Course }) {
  const pct = (course.completed / course.lessons) * 100;
  return (
    <div
      data-testid={`course-${course.id}`}
      className="ios26-card p-3 active:scale-[0.99] transition cursor-pointer"
    >
      <div className="flex gap-3">
        <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-[14px] text-[32px]" style={{ backgroundColor: course.accent + '18' }}>
          {course.cover}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="rounded-full bg-tf-surface-1 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-tf-text-3">{course.category}</span>
            {course.locked && <span className="rounded-full bg-tf-text-1 px-1.5 py-0.5 text-[9px] font-semibold text-white">🔒 {course.requiredTier}</span>}
            {course.completed === course.lessons && <span className="rounded-full bg-[#34C759] px-1.5 py-0.5 text-[9px] font-semibold text-white">✓ DONE</span>}
          </div>
          <div className="mt-0.5 text-[14px] font-bold text-tf-text-1 line-clamp-1">{course.title}</div>
          <div className="text-[11px] text-tf-text-3 line-clamp-1">{course.description}</div>
          <div className="mt-1.5 flex items-center gap-2 text-[11px]">
            <span className="text-tf-text-3 tabular-nums">★ {course.rating}</span>
            <span className="text-tf-text-3">{course.enrolled.toLocaleString()} students</span>
            <span className="text-tf-text-3">{course.duration}</span>
          </div>
          {course.completed > 0 && course.completed < course.lessons && (
            <div className="mt-1.5 h-1 w-full overflow-hidden rounded-full bg-tf-surface-1">
              <div className="h-full rounded-full bg-tf-blue" style={{ width: pct + '%' }} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
